import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Typography,
  CircularProgress,
  Box,
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import { createShareLink } from '../../services/reportService';
import showNotification from '../common/Notification';

/**
 * Dialog to generate a read-only share link with expiry selection
 */
const ShareReportDialog = ({ open, onClose, reportId, reportName }) => {
  const [expiryDays, setExpiryDays] = useState(7);
  const [shareUrl, setShareUrl] = useState('');
  const [expiresAt, setExpiresAt] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleGenerate = async () => {
    if (!reportId) return;

    try {
      setLoading(true);
      const result = await createShareLink(reportId, expiryDays);
      const url = `${window.location.origin}/shared/${result.data.token}`;
      setShareUrl(url);
      setExpiresAt(result.data.expiresAt);
      showNotification('Paylaşım linki oluşturuldu', 'success');
    } catch {
      showNotification('Link oluşturulamadı', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!shareUrl) return;

    try {
      await navigator.clipboard.writeText(shareUrl);
      showNotification('Link panoya kopyalandı', 'success');
    } catch {
      showNotification('Kopyalama başarısız', 'error');
    }
  };

  const handleClose = () => {
    setShareUrl('');
    setExpiresAt(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: '#240B4B' }}>
        Raporu Paylaş
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {reportName} — salt okunur paylaşım linki oluşturun. Giriş yapmadan görüntülenebilir.
        </Typography>

        <TextField
          select
          label="Geçerlilik süresi"
          value={expiryDays}
          onChange={(e) => setExpiryDays(Number(e.target.value))}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          disabled={!!shareUrl}
        >
          <MenuItem value={7}>7 gün</MenuItem>
          <MenuItem value={30}>30 gün</MenuItem>
        </TextField>

        {shareUrl && (
          <Box sx={{ mt: 1 }}>
            <TextField
              label="Paylaşım linki"
              value={shareUrl}
              fullWidth
              size="small"
              InputProps={{ readOnly: true }}
            />
            {expiresAt && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Son geçerlilik: {new Date(expiresAt).toLocaleString('tr-TR')}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>Kapat</Button>
        {!shareUrl ? (
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <ShareIcon />}
            sx={{ bgcolor: '#240B4B', '&:hover': { bgcolor: '#3d1c6b' } }}
          >
            {loading ? 'Oluşturuluyor...' : 'Link Oluştur'}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleCopy}
            sx={{ bgcolor: '#240B4B', '&:hover': { bgcolor: '#3d1c6b' } }}
          >
            Linki Kopyala
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ShareReportDialog;
